<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <div class="flex-grow-1">
                    <router-link :to="'/admissions/year/' + year" class="mr-3">
                        <icon name="chevron-left"/> Back
                    </router-link>
                    <strong>{{ weekCode }}</strong>
                    <span class="text-muted ml-2">— Admissions per cinema</span>
                </div>
                <b-form-select v-model="saleshouse"
                               :options="saleshouseOptions"
                               class="transparent-input shx-th-120"
                               @change="loadSites"/>
            </div>
        </div>

        <div v-if="loading" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="!rows.length" class="box">
            <div class="box-content text-center text-muted py-4">No admissions data found for {{ weekCode }}.</div>
        </div>

        <div v-if="!loading && previousWeek" class="box">
            <div class="box-content">
                <div v-if="!previousWeek.missing.length && !previousWeek.extra.length" class="text-muted">
                    <icon name="check"/> Same {{ rows.length }} cinemas as
                    <router-link :to="previousWeekLink">{{ previousWeek.week_code }}</router-link>.
                </div>
                <div v-else>
                    <div class="mb-1">
                        Compared to
                        <router-link :to="previousWeekLink">{{ previousWeek.week_code }}</router-link>
                        ({{ previousWeek.cinemas }} cinemas → {{ rows.length }} cinemas):
                    </div>
                    <div v-if="previousWeek.missing.length" class="alert alert-danger mb-2 py-2">
                        <strong>⚠ Missing {{ previousWeek.missing.length }}
                            {{ previousWeek.missing.length === 1 ? 'cinema' : 'cinemas' }}</strong>
                        (reported in {{ previousWeek.week_code }}, no data in {{ weekCode }}):
                        <ul class="mb-0 mt-1">
                            <li v-for="c in previousWeek.missing" :key="'missing-' + c.theatre_code">
                                <strong>{{ c.theatre_code }}</strong>
                                {{ c.theatre_name || '—' }}<span v-if="c.city">, {{ c.city }}</span>
                                <span class="text-muted">— {{ formatInt(c.admissions) }} admissions in {{ previousWeek.week_code }}</span>
                            </li>
                        </ul>
                        <div class="mt-2 small">
                            Double-check by verifying in
                            <a href="https://bitbucket.org/brightfishbe/cinedata_raw/" target="_blank" rel="noopener">cinedata_raw</a>
                            whether Cinedata delivered files for these cinemas between
                            <strong>{{ bucket.week_start || '?' }}</strong> and <strong>{{ bucket.week_end || '?' }}</strong>
                            ({{ weekCode }}).
                        </div>
                    </div>
                    <div v-if="previousWeek.extra.length" class="alert alert-info mb-0 py-2">
                        <strong>+ {{ previousWeek.extra.length }} new
                            {{ previousWeek.extra.length === 1 ? 'cinema' : 'cinemas' }}</strong>
                        (not reported in {{ previousWeek.week_code }}):
                        <ul class="mb-0 mt-1">
                            <li v-for="c in previousWeek.extra" :key="'extra-' + c.theatre_code">
                                <strong>{{ c.theatre_code }}</strong>
                                {{ c.theatre_name || '—' }}<span v-if="c.city">, {{ c.city }}</span>
                                <span class="text-muted">— {{ formatInt(c.admissions) }} admissions</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!loading && rows.length" class="box">
            <div class="box-content">
                <table class="shx-table w-100 mt-2">
                    <thead>
                        <tr>
                            <th class="shx-th-80">Code</th>
                            <th>Cinema</th>
                            <th class="shx-th-140">City</th>
                            <th class="text-right">Admissions</th>
                            <th class="text-right shx-th-100"># Titles</th>
                            <th class="text-right shx-th-100">Real %</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="row.theatre_code">
                            <td>{{ row.theatre_code }}</td>
                            <td>
                                {{ row.theatre_name || '—' }}
                                <span v-if="newCinemas[row.theatre_code]"
                                      class="badge badge-info ml-1"
                                      :title="'Not reported in ' + previousWeek.week_code">new</span>
                            </td>
                            <td>{{ row.city || '—' }}</td>
                            <td class="text-right">{{ formatInt(row.admissions) }}</td>
                            <td class="text-right">{{ row.titles }}</td>
                            <td class="text-right">
                                <span>{{ row.real_weighted != null ? row.real_weighted + '%' : '—' }}</span>
                                <span v-if="row.real_weighted != null && row.real_weighted <= 90"
                                      class="badge badge-warning ml-1"
                                      :title="'Minimum real: ' + row.real_min + '%'">⚠</span>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="3"><strong>Total</strong></td>
                            <td class="text-right"><strong>{{ formatInt(totalAdmissions) }}</strong></td>
                            <td colspan="2"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import {BFormSelect} from 'bootstrap-vue';
import axios from 'axios';

export default {
    name: 'AdmissionsYearWeekSites',
    components: {
        BFormSelect,
    },
    props: ['year', 'weekCode'],
    data() {
        return {
            loading: true,
            rows: [],
            bucket: {},
            previousWeek: null,
            saleshouse: this.$route.query.saleshouse || 'BF',
            saleshouseOptions: [
                {value: 'BF', text: 'BF'},
                {value: 'all', text: 'All'},
            ],
        };
    },
    computed: {
        totalAdmissions() {
            return this.rows.reduce((sum, r) => sum + (r.admissions || 0), 0);
        },
        newCinemas() {
            const map = {};
            (this.previousWeek?.extra || []).forEach(c => { map[c.theatre_code] = true; });
            return map;
        },
        previousWeekLink() {
            const year = this.previousWeek ? this.previousWeek.week_code.substring(0, 4) : this.year;
            return {
                name: 'AdmissionsYearWeekSites',
                params: {year, weekCode: this.previousWeek?.week_code},
                query: {saleshouse: this.saleshouse},
            };
        },
    },
    created() {
        this.loadSites();
    },
    watch: {
        weekCode() {
            this.loadSites();
        },
    },
    methods: {
        loadSites() {
            this.loading = true;
            this.$router.replace({
                name: 'AdmissionsYearWeekSites',
                params: {year: this.year, weekCode: this.weekCode},
                query: {saleshouse: this.saleshouse},
            }).catch(() => {});

            axios
                .get(`/admissions/week/${this.weekCode}/cinemas`, {
                    params: {saleshouse: this.saleshouse},
                })
                .then(response => {
                    this.rows = response.data.data.rows || [];
                    this.bucket = response.data.data.bucket || {};
                    this.previousWeek = response.data.data.previous_week || null;
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        formatInt(v) {
            return Math.round(v || 0).toLocaleString();
        },
    },
};
</script>
